/**
 * Screen 12 — "Does the value show up". The partner puts the TEST snippet on
 * a staging page (or localhost — test keys allow it), files a request, and
 * this screen follows it through: impression → lead in the inbox (badged
 * test, excluded from the dashboard) → deliveries to their destinations.
 * Test leads never reach contractors or the Finderly network.
 *
 * The step is done the moment a test lead exists — the data decides, as it
 * does for Verify (an impression landed). It used to complete only on the
 * footer's "Go to dashboard", so a partner who watched all three checks go
 * green and then left through the sidebar was told "Next: Test a lead"
 * forever. The Dashboard applies the same rule (Dashboard.tsx).
 *
 * It polls rather than streams: the API has no event stream, and a 5 s poll
 * of three cheap reads is indistinguishable at this scale.
 */
import { useEffect } from 'react';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { testSnippet } from '../install/snippets';
import { Link } from '../router';
import { OnboardingPage } from '../setup/OnboardingPage';
import { useProgress } from '../setup/progress';
import { destinationLabel, useDestinationLabels } from '../state/destination-labels';
import { useSites } from '../state/sites';
import { useLastSeen } from '../state/lastSeen';
import { CodeBlock, Empty, Loading, Time, humanize } from '../ui/kit';
import { useQuery } from '../ui/hooks';

/** "email ops@x.com (email) and hooks.x.com (webhook)" from labels like "email ops@x.com" / "webhook hooks.x.com". */
export function summarizeDestinations(labels: string[]): string {
  const parts = labels.map((l) => {
    const [kind, ...rest] = l.split(' ');
    return rest.length ? `${rest.join(' ')} (${kind})` : l;
  });
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function Check({ ok, skipped, label, children }: { ok: boolean; skipped?: boolean; label: string; children?: React.ReactNode }) {
  return (
    <li className="row" style={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}>
      <span className={`dotstat ${ok || skipped ? 'ok' : 'warn'}`} aria-hidden="true" />
      <div className="stack-sm" style={{ gap: 2 }}>
        <strong style={{ color: 'var(--ink)' }}>
          {label} <span className="small muted" style={{ fontWeight: 400 }}>{ok ? '· done' : skipped ? '· skipped' : '· waiting'}</span>
        </strong>
        {children}
      </div>
    </li>
  );
}

export function TestLead() {
  const { api, role } = useSession();
  const { current, reload: reloadSites } = useSites();
  const testKey = current?.test?.site_key ?? '';
  useQuery(() => reloadSites(), [testKey], { enabled: Boolean(testKey), pollMs: 5_000 });
  const leads = useQuery(() => api.listLeads({ mode: 'test', limit: '5' }), [testKey], { pollMs: 5_000 });
  const latest = leads.data?.items[0];
  const timeline = useQuery(() => api.leadTimeline(latest?.id ?? ''), [latest?.id], { enabled: Boolean(latest), pollMs: 5_000 });
  const deliveries = (timeline.data?.items ?? []).filter((t) => t.type === 'delivered');
  const destinationOf = useDestinationLabels();
  // Where a test lead will go: the active destinations, named. Only owners and
  // admins may read destinations; an operator sees the generic line.
  const canSeeDestinations = can(role, 'destinations');
  const destinations = useQuery(() => api.listDestinations(), [], { enabled: canSeeDestinations });
  const active = (destinations.data?.items ?? []).filter((d) => d.status === 'active');
  const lastSeen = useLastSeen();
  const impression = lastSeen.site(current?.test?.site_key, current?.test?.last_impression_at);
  const { markDone, refresh } = useProgress();
  // The step is done the moment a test lead exists (setup/status.ts): re-read now so the rail says so.
  const hasLead = Boolean(latest);
  useEffect(() => {
    if (!hasLead) return;
    markDone('test');
    refresh();
  }, [hasLead, markDone, refresh]);

  return (
    <OnboardingPage slug="test" lede="Run one request end to end with your test key. Test leads are labelled, never sent to contractors, and left out of your dashboard.">
      {!current?.test ? (
        <Empty title="This site has no test key" />
      ) : (
        <>
          <section className="stack">
            <h2>1 · Put the test snippet on a staging page</h2>
            <p className="muted">Any page on your website works, and so does http://localhost for a quick try.</p>
            <CodeBlock caption="Test snippet" code={testSnippet(current.test.site_key)} />
          </section>
          <section className="panel panel-pad stack">
            <h2>2 · Send a request through it</h2>
            <ol className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }} aria-live="polite">
              {/* A lead can exist with no widget load: an AI agent (Claude Code) files
                  one straight through the API. Then this step was skipped, not pending
                  (third live review, 2026-09-27: it read "waiting" under two done steps). */}
              <Check ok={Boolean(impression)} skipped={!impression && Boolean(latest)} label="Widget loaded with the test key">
                <span className="small muted">
                  {impression ? (
                    <>
                      Last seen <Time iso={impression} />
                    </>
                  ) : latest ? (
                    'Skipped: the test lead was sent directly (e.g. by Claude Code), not through the widget. Put the test snippet on a page to see the widget itself.'
                  ) : (
                    'Waiting for the widget to load on your page…'
                  )}
                </span>
              </Check>
              <Check ok={Boolean(latest)} label="Lead in your inbox">
                {leads.loading ? (
                  <Loading />
                ) : latest ? (
                  <span className="small">
                    <Link to={`/leads/${latest.id}`}>
                      {latest.problem_label ?? humanize(latest.trade)}
                    </Link>{' '}
                    · <Time iso={latest.created_at} /> · <span className="tag test">test</span>
                  </span>
                ) : (
                  <span className="small muted">Fill in the contact form in the widget and send it.</span>
                )}
              </Check>
              <Check ok={deliveries.length > 0} label="Delivered to your destinations">
                {deliveries.length ? (
                  <ul className="small" style={{ margin: 0, paddingLeft: 16 }}>
                    {deliveries.map((d, i) => (
                      <li key={i}>
                        {summarizeDestinations([destinationOf(d.detail.destination_id)].filter(Boolean)) || 'Your destination'}: {humanize(String(d.detail.status))}
                        {d.detail.response_code ? ` (HTTP ${String(d.detail.response_code)})` : ''}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="small muted">
                    {latest ? 'Waiting for it to be sent…' : 'Appears after the lead.'}{' '}
                    {active.length ? (
                      <>Will be sent to {summarizeDestinations(active.map(destinationLabel))}.</>
                    ) : canSeeDestinations && destinations.data ? (
                      <>
                        No destination yet? <Link to="/setup/routing">Add one</Link>.
                      </>
                    ) : null}
                  </span>
                )}
              </Check>
            </ol>
          </section>
        </>
      )}
    </OnboardingPage>
  );
}
